import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, CopySimple, Trash, CheckCircle, Eye } from "@phosphor-icons/react";
import api from "@/lib/api";
import CountryPicker, { countryName } from "@/pages/admin/CountryPicker";
import { useClashGuard } from "@/pages/admin/ThemeClashDialog";

const B = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function SeasonalThemeCard({ t, onChanged }) {
  const [prompt, setPrompt] = useState(t.last_prompt || "");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ name: t.name, idea: t.idea || "", starts_on: t.starts_on, ends_on: t.ends_on, countries: t.countries || [] });
  const id = t.theme_id;

  const call = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChanged(); return true; } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); return false; }
  };
  const generate = () => call(() => api.post(`/admin/seasonal-themes/${id}/generate`, { prompt }), t.enabled ? "Creating new artwork — it goes back to Draft until you approve it" : "Creating artwork — about 30 seconds");
  const { guard, dialog } = useClashGuard();
  const save = () => guard(form, id, () => call(() => api.patch(`/admin/seasonal-themes/${id}`, form), "Saved").then((ok) => ok && setEdit(false)));
  const approve = (enabled) => call(() => api.patch(`/admin/seasonal-themes/${id}`, { enabled }), enabled ? "Approved — it will show on its dates" : "Unapproved");
  const copy = () => call(() => api.post(`/admin/seasonal-themes/${id}/copy-next-year`), "Copied to next year — check the dates");
  const del = () => window.confirm(`Delete "${t.name}"?`) && call(() => api.delete(`/admin/seasonal-themes/${id}`), "Deleted");
  const busy = t.gen_status === "generating";

  return (
    <div className="card-recipe !p-5" data-testid={`theme-card-${id}`}>
      {dialog}
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="flex-1 min-w-[200px]">
          <p className="font-display text-xl font-black text-rf-ink">{t.name}</p>
          <p className="text-xs text-rf-ink-soft" data-testid={`theme-dates-${id}`}>
            {fmt(t.starts_on)} – {fmt(t.ends_on)} · {t.countries?.length ? t.countries.map(countryName).join(", ") : "Worldwide (all countries)"}
          </p>
        </div>
        <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`} data-testid={`theme-status-${id}`}>
          {t.enabled ? (t.window_status === "running" ? "Live now" : t.window_status === "ended" ? "Ended" : "Scheduled") : "Draft"}
        </span>
      </div>

      <div className="grid md:grid-cols-[180px_1fr] gap-4 mb-4">
        <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-rf-cream-alt flex items-center justify-center text-xs text-rf-ink-soft">
          {busy ? "Creating…" : t.hero_url ? <img src={`${B}${t.hero_url}`} alt={`${t.name} hero`} className="w-full h-full object-cover" data-testid={`theme-hero-${id}`} /> : "No artwork yet"}
        </div>
        <div className="flex flex-col gap-3">
          <div className="h-24 rounded-xl bg-rf-cream-alt bg-repeat-x" data-testid={`theme-strip-${id}`}
               style={t.strip_url && !busy ? { backgroundImage: `url(${B}${t.strip_url})`, backgroundSize: "auto 300%", backgroundPosition: "center" } : {}} />
          {t.gen_status === "failed" && <p className="text-xs text-red-600">{t.gen_error}</p>}
          <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid={`theme-prompt-${id}`}
                    placeholder="Optional direction for the AI, e.g. 'more pastel, add a gingerbread house, less spooky'"
                    className="input-rf text-sm" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={generate} disabled={busy} data-testid={`theme-generate-${id}`} className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-1 disabled:opacity-50">
              <Sparkle size={14} /> {busy ? "Creating…" : t.hero_url ? "Regenerate" : "Generate artwork"}
            </button>
            {t.hero_url && !busy && (
              <button type="button" onClick={() => approve(!t.enabled)} data-testid={`theme-approve-${id}`} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1">
                <CheckCircle size={14} /> {t.enabled ? "Unapprove" : "Approve & schedule"}
              </button>
            )}
            {t.hero_url && !busy && (
              <a href={`/?preview_theme=${id}`} target="_blank" rel="noopener noreferrer" data-testid={`theme-preview-${id}`} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1">
                <Eye size={14} /> Preview on site
              </a>
            )}
            <button type="button" onClick={() => setEdit((v) => !v)} data-testid={`theme-edit-${id}`} className="btn-outline-ink !py-2 !px-4 text-sm">Dates & countries</button>
            <button type="button" onClick={copy} data-testid={`theme-copy-${id}`} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1"><CopySimple size={14} /> Copy to next year</button>
            <button type="button" onClick={del} data-testid={`theme-delete-${id}`} className="text-red-600 text-sm font-bold inline-flex items-center gap-1 px-2"><Trash size={14} /> Delete</button>
          </div>
        </div>
      </div>

      {edit && (
        <div className="grid md:grid-cols-2 gap-3 border-t border-rf-ink/10 pt-4" data-testid={`theme-edit-form-${id}`}>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-rf" data-testid={`theme-name-${id}`} />
          <input value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} placeholder="What should the art show?" className="input-rf" data-testid={`theme-idea-${id}`} />
          <label className="text-xs font-bold">Starts (UK)<input type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} className="input-rf mt-1" data-testid={`theme-start-${id}`} /></label>
          <label className="text-xs font-bold">Ends (UK)<input type="date" value={form.ends_on} min={form.starts_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} className="input-rf mt-1" data-testid={`theme-end-${id}`} /></label>
          <div className="md:col-span-2"><CountryPicker value={form.countries} onChange={(c) => setForm({ ...form, countries: c })} testid={`theme-countries-${id}`} /></div>
          <button type="button" onClick={save} className="btn-amber !py-2 !px-4 text-sm md:col-span-2 justify-self-start" data-testid={`theme-save-${id}`}>Save</button>
        </div>
      )}
    </div>
  );
}
